// @ts-check

import { useCallback, useEffect, useRef, useState } from "react"
import {
  getCategoriesOfTasksOfTeamMemberId,
  getLazyIndividualReportData,
  getModulesOfTasksOfTeamMemberId,
  getProjectsOfTasksOfTeamMemberId,
} from "../../../../services/report.service"
import useToast from "../../../../hooks/useToast.hook"
import {
  formatCategoriesDropdownFilter,
  formatModulesDropdownFilter,
  formatProjectsDropdownFilter,
} from "../../../../utils/report.utils"
import { getAllTypeDayOffHolidaySchemaRules } from "../../../../services/vacation.service"
import { DateTime } from "luxon"
import { usePaginatedFetch } from "../../../../hooks/usePaginatedFetch.hook"

/**
 * @param {import("./useIndividualReportTableData.hook").useIndividualReportTableDataParams} params
 */
export function useIndividualReportTableData(params) {
  const { teamMember, dateRange, filterValues = {} } = params
  const teamMemberId = teamMember?.id
  const holidaySchemaId = teamMember?.idHolidaySchema
  const [startDate, endDate] = dateRange

  const [categories, setCategories] = useState(
    /** @type {import("../../../../utils/report.utils").FormattedCategory[]} */ ([])
  )
  const [modules, setModules] = useState(
    /** @type {import("../../../../utils/report.utils").FormattedModule[]} */ ([])
  )
  const [projects, setProjects] = useState(
    /** @type {import("../../../../utils/report.utils").ProjectGroup[]} */ ([])
  )
  const [schemaRules, setSchemaRules] = useState([])

  const [isLoadingMeta, setIsLoadingMeta] = useState(false)
  const [isLoadingSchemaRules, setIsLoadingSchemaRules] = useState(false)

  const metaRequestIdRef = useRef(0)

  const { setErrorMessage } = useToast()

  /**
   * @param {{
   *   tasks: import("../../../../services/reports.service.types").BaseTaskReportItem[]
   *   holidays: import("../../../../services/reports.service.types").HolidayDayItem[]
   *   vacations: import("../../../../services/reports.service.types").VacationDayItem[]
   * }} result
   * @param {boolean} [isFiltered]
   */
  function buildTasksBatch(result, isFiltered = false) {
    const { tasks = [], holidays = [], vacations = [] } = result

    const tasksByDate = new Map()
    const specialDays = new Map()

    tasks.forEach((task) => {
      const dateKey = task.date
      if (!tasksByDate.has(dateKey)) {
        tasksByDate.set(dateKey, [])
      }
      tasksByDate.get(dateKey).push(task)
    })

    const placeholders =
      /** @type {import("../../../../services/reports.service.types").SpecialDayPlaceholder[]} */ ([])

    holidays.forEach((holiday) => {
      const dateKey = holiday.date
      specialDays.set(dateKey, { ...holiday, isHoliday: true })
      if (!tasksByDate.has(dateKey)) {
        placeholders.push({
          ...holiday,
          taskId: `holiday-placeholder-${dateKey}`,
          isPlaceholder: true,
          isHoliday: true,
        })
      }
    })

    vacations.forEach((vacation) => {
      const dateKey = vacation.date
      specialDays.set(dateKey, { ...vacation, isVacationDay: true })
      if (!tasksByDate.has(dateKey)) {
        placeholders.push({
          ...vacation,
          taskId: `vacation-placeholder-${dateKey}`,
          isPlaceholder: true,
          isVacationDay: true,
        })
      }
    })

    const tasksWithSpecialDays = tasks.map((task) => {
      const specialDay = specialDays.get(task.date)
      if (!specialDay) return task

      return {
        ...task,
        ...specialDay,
        hasTasksInDate: true,
        isPlaceholder: false,
      }
    })

    return [...tasksWithSpecialDays, ...(isFiltered ? [] : placeholders)]
  }

  const {
    items: tasks,
    totals,
    hasNextPage,
    isLoading: isLoadingTasks,
    isFetchingNextPage,
    fetch: fetchTasks,
    reset: resetPagination,
    abort,
  } = usePaginatedFetch({
    fetchFn: async ({ cursor, signal }) => {
      const response = await getLazyIndividualReportData({
        params: {
          teamMemberId,
          startDate,
          endDate,
          cursorDate: cursor,
          filters: filterValues,
        },
        config: { signal },
      })

      const {
        tasks: newTasks = [],
        holidays: newHolidays = [],
        vacations: newVacations = [],
        totals: rawTotals,
        cursor: responseCursor,
      } = response.result ?? {}

      const batch = buildTasksBatch(
        {
          tasks: newTasks,
          holidays: newHolidays,
          vacations: newVacations,
        },
        filterValues && Object.values(filterValues).some((value) => value)
      )

      const hasNewTasks = batch.length > 0

      return {
        batch,
        hasMore: hasNewTasks && Boolean(responseCursor?.hasNextPage),
        nextCursor:
          hasNewTasks && responseCursor?.nextCursor ?
            DateTime.fromISO(responseCursor.nextCursor.split("T")[0])
          : null,
        totals: {
          sisuTime: rawTotals?.sisuTime ?? null,
          billedTime: rawTotals?.billedTime ?? null,
          timeDifference: rawTotals?.timeDifference ?? null,
        },
      }
    },
    onError: (error) => setErrorMessage({ message: error.message }),
    getItemKey: (item) => item.taskId,
  })

  const fetchMeta = useCallback(async () => {
    if (!teamMemberId || !startDate || !endDate) return

    const requestId = ++metaRequestIdRef.current
    setIsLoadingMeta(true)

    try {
      const [catRes, modRes, projRes] = await Promise.all([
        getCategoriesOfTasksOfTeamMemberId(teamMemberId, startDate, endDate),
        getModulesOfTasksOfTeamMemberId(teamMemberId, startDate, endDate),
        getProjectsOfTasksOfTeamMemberId(teamMemberId, startDate, endDate),
      ])

      if (metaRequestIdRef.current !== requestId) return

      setCategories(formatCategoriesDropdownFilter(catRes.result, true))
      setModules(formatModulesDropdownFilter(modRes.result))
      setProjects(formatProjectsDropdownFilter(projRes.result))
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage({ message: error.message })
      }
    } finally {
      if (metaRequestIdRef.current === requestId) {
        setIsLoadingMeta(false)
      }
    }
  }, [teamMemberId, startDate, endDate])

  const fetchSchemaRules = useCallback(async () => {
    if (!holidaySchemaId) return

    setIsLoadingSchemaRules(true)

    try {
      const response = await getAllTypeDayOffHolidaySchemaRules()
      if (!response.success) throw new Error(response.message)
      setSchemaRules(response.result?.[holidaySchemaId] ?? [])
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage({ message: error.message })
      }
    } finally {
      setIsLoadingSchemaRules(false)
    }
  }, [holidaySchemaId])

  const refreshData = useCallback(() => {
    resetPagination()
    fetchMeta()
    fetchTasks({ reset: true })
  }, [resetPagination, fetchMeta, fetchTasks])

  useEffect(() => {
    if (!teamMemberId || !startDate || !endDate) return

    resetPagination()
    fetchMeta()
    fetchTasks({ reset: true })

    return () => abort()
  }, [teamMemberId, startDate, endDate])

  useEffect(() => {
    if (!teamMemberId || !startDate || !endDate) return

    resetPagination()
    fetchTasks({ reset: true })
  }, [filterValues, teamMemberId, startDate, endDate])

  useEffect(() => {
    if (!holidaySchemaId) return
    fetchSchemaRules()
  }, [holidaySchemaId])

  return {
    tasks,
    totals,
    categories,
    modules,
    projects,
    schemaRules,
    isLoading: isLoadingTasks || isLoadingMeta || isLoadingSchemaRules,
    isFetchingNextPage,
    refreshData,
    resetData: resetPagination,
    fetchNextPage: fetchTasks,
    hasNextPage,
  }
}
